import { useMemo } from 'react';
import { Bot } from 'lucide-react';
import type { LaunchMentionProviderProps } from '@reflex/plugin-api';
import {
  MentionCommandItem,
  MentionCommandList,
} from '@reflex/ui/components/shared/MentionCommandList';
import {
  MentionDetail,
  MentionLoadingRow,
  type MentionDetailFact,
} from '@reflex/ui/components/shared/MentionRow';
import { timeAgo } from '@reflex/ui/lib/format';
import { extractAgentIdFromInput } from '@reflex/shared';
import { useAgent, useAgentSearch } from '@/hooks/agents/useAgents';

/**
 * How many runs to pull per search. Runs are the one mention source with no
 * natural bound — an org accumulates them forever — so the page size is fixed
 * here rather than derived from the menu's display budget, which is absent in a
 * scoped view. Sized to fill a scrolled list after archived runs drop out.
 */
const SEARCH_PAGE_SIZE = 20;

/**
 * Launch `@`-mention provider for agent runs. Selecting a run inserts a compact
 * reference token (`@[<title>](agent-run:<id>)`) into the composer rather than
 * configuring the run. The server expands that token into the referenced run's
 * full transcript before the prompt reaches the agent, so the chat shows a tidy
 * reference while the agent receives the whole context.
 *
 * Agent runs are a core entity (not plugin-owned), so this provider lives in
 * the host and is rendered directly by {@link LaunchMentionMenu}.
 */
export function AgentRunsMentionProvider({
  query,
  limit,
  onApply,
  groupHeading,
}: LaunchMentionProviderProps) {
  // A pasted agent id or agent URL should resolve straight to that run. The
  // server search matches only a run's name and prompt, so an id never
  // substring-matches — look the run up by id directly instead. This lets a
  // user reference a run by dropping in its id or a link to its page.
  const referencedId = useMemo(() => extractAgentIdFromInput(query), [query]);
  const { data: referencedAgent, isFetching: isFetchingReferenced } = useAgent(
    referencedId ?? undefined,
  );

  // Search the server so any run in the org is reachable — not just those on
  // the first page the client happens to hold. The server matches the query
  // against each run's name and prompt. Skip the search entirely when the
  // query is an id/URL we resolve directly above.
  const { data: agents = [], isFetching: isFetchingSearch } = useAgentSearch(
    query,
    SEARCH_PAGE_SIZE,
    { enabled: !referencedId },
  );

  const matches = useMemo(() => {
    // A direct id/URL reference resolves to exactly that run — surface it even
    // when archived, since the user named it explicitly.
    if (referencedId) return referencedAgent ? [referencedAgent] : [];
    return agents.filter((agent) => !agent.archived).slice(0, limit);
  }, [referencedId, referencedAgent, agents, limit]);

  const isFetching = referencedId ? isFetchingReferenced : isFetchingSearch;

  if (matches.length === 0) {
    if (isFetching)
      return <MentionLoadingRow label="Loading agent runs…" groupHeading={groupHeading} />;
    return null;
  }

  const items = matches.map((agent) => {
    const facts: MentionDetailFact[] = [
      { label: 'Status', value: agent.status.replace(/_/g, ' ') },
      { label: 'Started', value: timeAgo(agent.createdAt) },
      { label: 'Type', value: agent.agentType },
    ];
    return (
      <MentionCommandItem
        key={agent.id}
        value={`agent-run:${agent.id}`}
        keywords={[agent.name, agent.agentType]}
        onSelect={() =>
          onApply({ kind: 'reference-agent-run', agentId: agent.id, title: agent.name })
        }
        icon={<Bot />}
        name={agent.name}
        details={<MentionDetail name={agent.name} kind="Agent run" facts={facts} />}
      />
    );
  });

  return <MentionCommandList groupHeading={groupHeading}>{items}</MentionCommandList>;
}
